/**
 * TopNav — global top horizontal navigation, replacing the left sidebar
 * that previously lived in MainLayout.tsx. Institutional-trading-terminal
 * styling: compact typography, subtle border dividers, high contrast,
 * clean hover/active indicators (active = accent bottom border).
 *
 * Routes, permissions (`hasRole`), and auth actions (`logout`) are all
 * reused as-is from the existing AuthContext/react-router setup — this
 * component only changes WHERE navigation lives, never what it does or
 * which route/component each item resolves to.
 *
 * Nav item list: the 12 items from the redesign spec, PLUS "Options
 * Dashboard" (/options-dashboard) and "Stock Data" (/stocks) — both had
 * live sidebar entries before this redesign and were explicitly kept as
 * extra top-level items per user decision, so neither becomes orphaned
 * (URL-only) after the sidebar is removed.
 */
import { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import useAuth from "@/hooks/useAuth";
import useThemeMode from "@/hooks/useThemeMode";
import type { ModuleCode } from "@/types";

interface NavLinkItem {
  label: string;
  path: string;
  /** Only set for the 4 Options Analyst sub-views, which all share the
   * /options-analyst path and differ by ?tab= query param. */
  tab?: string;
  /** User Onboarding + Module Access (Pilot): item is hidden unless the
   * logged-in user has this module (or is admin/superuser). Undefined
   * means "always visible" (e.g. Dashboard, Option Analyst). */
  requiredModule?: ModuleCode;
}

const PRIMARY_NAV: NavLinkItem[] = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Option Analyst", path: "/options-analyst", requiredModule: "OPTION_ANALYST" },
  { label: "Broker Account", path: "/broker-accounts", requiredModule: "ALGO_STRATEGY" },
  { label: "Algo Strategies", path: "/strategies", requiredModule: "ALGO_STRATEGY" },
  { label: "Orders", path: "/orders", requiredModule: "ALGO_STRATEGY" },
  { label: "Positions", path: "/positions", requiredModule: "ALGO_STRATEGY" },
  { label: "Investing Idea", path: "/investing-idea", requiredModule: "INVESTMENT_IDEAS" },
];

const ADMIN_NAV: NavLinkItem[] = [
  { label: "Users", path: "/admin/users" },
  { label: "Roles", path: "/admin/roles" },
  { label: "Strategy Management", path: "/admin/strategies" },
  { label: "OI Backfill", path: "/admin/oi-backfill" },
  { label: "Data Operations", path: "/admin/data-operations" },
  { label: "Analytics & Insights", path: "/admin/analytics" },
  { label: "Monthly P&L Report", path: "/admin/reports/algo-pnl" },
  { label: "Pre-Market Diagnostics", path: "/admin/pre-market-diagnostics" },
  { label: "VM Maintenance", path: "/admin/vm-maintenance" },
  { label: "Algo Execution Monitor", path: "/admin/algo-execution-monitor" },
  { label: "RSI Momentum", path: "/admin/strategies/rsi-momentum" },
  { label: "DEMA-WMA Gainer", path: "/admin/strategies/dema-wma-gainer" },
  { label: "Enhanced DEMA-WMA v33", path: "/admin/strategies/dema-wma-gainer-enhanced" },
];

const KNOWN_ANALYST_TABS = ["indicator", "charts", "strike-charts", "dual"];

// ---- Design tokens ported from Super Trading's production stylesheet
// (react-native-web atomic classes: `.css-11aywtz`/`.css-146c3p1` for the
// font stack, `.r-1xfd6ze` for the 8px "chip"-scale radius, `.r-1dzdj1l`
// for the 10px "card"-scale radius, `.r-1sp51qo`/`.r-edyy15`/`.r-3o4zer`
// for the 10px/8px/12px padding scale). Colors intentionally stay on this
// app's own theme tokens (`text.primary`, `primary.main`, `action.hover`,
// ...) rather than Super Trading's literal `rgba(0,0,0,1.00)` black-on-
// white values, so TekInvest's existing light/dark mode toggle keeps
// working exactly as before — only the shape/spacing/typography tokens
// are ported verbatim, not a hardcoded light-only palette.
const FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const RADIUS_CHIP = "8px"; // .r-1xfd6ze
const RADIUS_CARD = "10px"; // .r-1dzdj1l
const PAD_LOOSE = "10px"; // .r-1sp51qo
const PAD_TIGHT = "8px"; // .r-edyy15
const PAD_INLINE = "12px"; // .r-3o4zer

function linkTo(item: NavLinkItem): string {
  return item.tab ? `${item.path}?tab=${item.tab}` : item.path;
}

function isItemActive(pathname: string, tabParam: string | null, item: NavLinkItem): boolean {
  if (pathname !== item.path) return false;
  if (!item.tab) return true;
  // OptionsAnalystPage itself treats an absent/unrecognized ?tab= as
  // "indicator" (see its own useState initializer) — mirror that so the
  // "Option Indicator" link still reads as active with no query param.
  const effectiveTab = KNOWN_ANALYST_TABS.includes(tabParam ?? "") ? tabParam : "indicator";
  return effectiveTab === item.tab;
}

// Super Trading's interactive-state philosophy: a filled, fully-rounded
// "chip" for the active item (not an underline) and a soft rounded hover
// fill for inactive items — replaces this component's previous flat/
// square bottom-border indicator.
// 2026-09-13: nav label font size set to an explicit spec (18px) rather
// than the theme's own sizing. The color change that briefly accompanied
// this (a hardcoded #000000) was reverted the same day — reported as
// still not fixing readability (see git history) — so color is back to
// the original theme tokens (`text.primary`/`primary.contrastText`),
// which adapt correctly to both light and dark mode and to the active
// item's filled chip background. Only `fontSize` differs from the
// pre-2026-09-13 styling now.
const navButtonSx = (active: boolean) => ({
  flexShrink: 0,
  px: PAD_INLINE,
  py: "6px",
  minWidth: "auto",
  fontFamily: FONT_STACK,
  fontSize: "18px",
  fontWeight: active ? 700 : 600,
  color: active ? "primary.contrastText" : "text.primary",
  bgcolor: active ? "primary.main" : "transparent",
  borderRadius: RADIUS_CHIP,
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const,
  transition: "background-color 0.15s ease, color 0.15s ease",
  "&:hover": {
    bgcolor: active ? "primary.dark" : "action.hover",
    color: active ? "primary.contrastText" : "primary.main",
  },
});

export function TopNav() {
  const { user, hasRole, hasModule, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get("tab");
  const visibleNav = PRIMARY_NAV.filter((item) => !item.requiredModule || hasModule(item.requiredModule));

  const [adminAnchorEl, setAdminAnchorEl] = useState<HTMLElement | null>(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(null);

  const isAdmin = hasRole("ADMIN");
  const adminActive = isAdmin && ADMIN_NAV.some((item) => item.path === location.pathname);

  // Same logout flow as the old sidebar footer: clear auth state, then
  // route to "/" (the public landing page / HomeRoute).
  const handleLogout = async () => {
    setProfileAnchorEl(null);
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}
    >
      <Toolbar
        variant="dense"
        sx={{
          gap: PAD_TIGHT,
          minHeight: 56,
          px: PAD_LOOSE,
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 4 },
        }}
      >
        <Typography
          component={RouterLink}
          to="/dashboard"
          variant="subtitle1"
          fontWeight={700}
          color="primary"
          sx={{
            textDecoration: "none",
            mr: 2,
            flexShrink: 0,
            letterSpacing: 0.5,
            fontFamily: FONT_STACK,
            // 2026-09-13: font size only — the color change that
            // accompanied this was reverted the same day (see
            // `navButtonSx`'s own comment above); back to the theme's
            // `primary` color token via the `color` prop below.
            fontSize: "18px",
          }}
        >
          TekInvest
        </Typography>

        {/* Primary nav — horizontal scroll is this app's established
            overflow pattern for dense tab/nav bars (see OptionsAnalystPage's
            own scrollable Tabs), reused here instead of an auto-generated
            "More" menu. Rounded "chip" active/hover states + 8px gaps below
            mirror Super Trading's own horizontal-option-row spacing. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: PAD_TIGHT, flexGrow: 1, overflowX: "auto" }}>
          {visibleNav.map((item) => (
            <Button
              key={item.label}
              component={RouterLink}
              to={linkTo(item)}
              size="small"
              sx={navButtonSx(isItemActive(location.pathname, tabParam, item))}
            >
              {item.label}
            </Button>
          ))}

          {isAdmin && (
            <>
              <Button
                size="small"
                onClick={(e) => setAdminAnchorEl(e.currentTarget)}
                endIcon={<ArrowDropDownIcon fontSize="small" />}
                aria-haspopup="true"
                aria-expanded={Boolean(adminAnchorEl)}
                sx={navButtonSx(adminActive)}
              >
                Administrator
              </Button>
              <Menu
                anchorEl={adminAnchorEl}
                open={Boolean(adminAnchorEl)}
                onClose={() => setAdminAnchorEl(null)}
                slotProps={{ paper: { sx: { borderRadius: RADIUS_CARD, mt: 0.5 } } }}
              >
                {ADMIN_NAV.map((item) => (
                  <MenuItem
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => setAdminAnchorEl(null)}
                    sx={{ fontFamily: FONT_STACK, fontSize: "14px", borderRadius: RADIUS_CHIP, mx: 0.5, my: 0.25 }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: PAD_TIGHT, flexShrink: 0, ml: 1 }}>
          {/* Deliberately OUTSIDE the scrollable primary-nav row above, not
              inside it: with 7 other items already competing for space
              there, "Help & Guide" as a trailing nav link was getting
              scrolled out of view entirely on ordinary screen widths (an
              always-visible ability to reach the guide was the actual
              requirement, not just a route existing somewhere in the nav
              list). A persistent icon here, matching the dark-mode toggle's
              own treatment, guarantees it's always reachable regardless of
              how many module-gated nav items a given user has. */}
          <Tooltip title="Help & Guide">
            <IconButton
              component={RouterLink}
              to="/help"
              size="small"
              aria-label="Help & Guide"
              sx={{
                borderRadius: RADIUS_CHIP,
                color: location.pathname === "/help" ? "primary.main" : "inherit",
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton size="small" onClick={toggleMode} aria-label="Toggle color mode" sx={{ borderRadius: RADIUS_CHIP }}>
              {mode === "dark" ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <IconButton
            size="small"
            onClick={(e) => setProfileAnchorEl(e.currentTarget)}
            aria-label="Account menu"
            sx={{ borderRadius: RADIUS_CHIP }}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={profileAnchorEl}
            open={Boolean(profileAnchorEl)}
            onClose={() => setProfileAnchorEl(null)}
            slotProps={{ paper: { sx: { borderRadius: RADIUS_CARD, mt: 0.5 } } }}
          >
            <MenuItem disabled sx={{ opacity: "1 !important", fontFamily: FONT_STACK, fontSize: "14px" }}>
              {user?.full_name}
            </MenuItem>
            <Divider />
            <MenuItem
              component={RouterLink}
              to="/dashboard"
              onClick={() => setProfileAnchorEl(null)}
              sx={{ fontFamily: FONT_STACK, fontSize: "14px", borderRadius: RADIUS_CHIP, mx: 0.5, my: 0.25 }}
            >
              Dashboard
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/profile"
              onClick={() => setProfileAnchorEl(null)}
              sx={{ fontFamily: FONT_STACK, fontSize: "14px", borderRadius: RADIUS_CHIP, mx: 0.5, my: 0.25 }}
            >
              My Profile
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/change-password"
              onClick={() => setProfileAnchorEl(null)}
              sx={{ fontFamily: FONT_STACK, fontSize: "14px", borderRadius: RADIUS_CHIP, mx: 0.5, my: 0.25 }}
            >
              Change Password
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/help"
              onClick={() => setProfileAnchorEl(null)}
              sx={{ fontFamily: FONT_STACK, fontSize: "14px", borderRadius: RADIUS_CHIP, mx: 0.5, my: 0.25 }}
            >
              Help &amp; Guide
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{ fontFamily: FONT_STACK, fontSize: "14px", borderRadius: RADIUS_CHIP, mx: 0.5, my: 0.25 }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default TopNav;
